import { cn } from "@newxsystems/shared";
import { Icon } from "@templates/design-system";
import type { AnchorHTMLAttributes, ReactNode } from "react";
import { getComponent } from "../../../../client";
import { BlockRichText } from "../../../shared/components/BlockRichText/BlockRichText";
import type { IIconContentGridCardProps } from "./IconContentGridCard.props";

const cardClasses =
  "group flex h-full min-w-0 flex-col items-start border-t border-foreground py-6 text-start [overflow-wrap:anywhere] md:py-8";

const renderContent = (
  icon: string,
  heading: string,
  description: string | null | undefined,
): ReactNode => (
  <>
    <Icon className="size-9 text-paprika" name={icon} />
    <h3 className="mt-8 max-w-[22ch] text-balance text-[24px]/[1.15] font-semibold tracking-[-0.02em]">
      {heading}
    </h3>
    {description ? (
      <BlockRichText
        className="mt-5 max-w-[65ch] text-base/[1.55] text-pretty text-onSurface-500 [&_a]:underline [&_ol]:ml-5 [&_ol]:list-decimal [&_p+p]:mt-4 [&_ul]:ml-5 [&_ul]:list-disc"
        content={description}
      />
    ) : null}
  </>
);

export const IconContentGridCard = ({
  cardLink,
  description,
  heading,
  icon,
}: IIconContentGridCardProps) => {
  const content = renderContent(icon, heading, description);

  if (!cardLink) {
    return <article className={cardClasses}>{content}</article>;
  }

  const Link = getComponent<AnchorHTMLAttributes<HTMLAnchorElement>>("Link");

  if (!Link) {
    return null;
  }

  const opensNewTab = cardLink.openInNewTab === true;

  return (
    <article className={cn(cardClasses, "relative border-electric-blue")}>
      <Link
        aria-label={cardLink.accessibilityLabel ?? cardLink.label}
        className="absolute inset-0 z-0 transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-ring"
        href={cardLink.href}
        rel={opensNewTab ? "noopener noreferrer" : undefined}
        target={opensNewTab ? "_blank" : undefined}
      />
      <div className="relative z-10 pointer-events-none [&_a]:pointer-events-auto [&_a]:relative [&_a]:z-20">
        {content}
      </div>
    </article>
  );
};

IconContentGridCard.displayName = "IconContentGrid::IconContentGridCard";
